import { useEffect, useMemo, useState } from "react";
import { Smile, History as HistoryIcon, ArrowUpRight, FileText } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import ToothChart from "./ToothChart";
import { defaultDentitionFromDOB } from "./dentition";
import { procedureColor } from "@/components/treatment/procedureColors";
import {
  applyCompletedWorkChartEffects,
  buildCompletedWorkByTooth,
} from "./completedWorkChartState";

// Plan / item statuses that count as "open work" for the Overview dots.
// Excludes Rejected / Superseded plans, and Declined / Completed items.
const ACTIVE_PLAN_STATUSES = new Set(["Draft", "Proposed", "Accepted", "Partially Accepted"]);
const OPEN_ITEM_STATUSES   = new Set(["Candidate", "Proposed", "Accepted", "Deferred", "Scheduled"]);

// Build { "16": ["#3b82f6", "#8b5cf6"], ... } — one unique color per
// open procedure category on each tooth in the active plan.
function buildPlanDotsByTooth(items) {
  if (!items?.length) return null;
  const out = {};
  for (const it of items) {
    if (!OPEN_ITEM_STATUSES.has(it.status)) continue;
    const tooth = it.tooth_number;
    if (!tooth) continue;
    const key = String(tooth);
    const color = procedureColor(it.procedure_category);
    if (!out[key]) out[key] = [];
    if (!out[key].includes(color)) out[key].push(color);
  }
  return Object.keys(out).length ? out : null;
}

// =====================================================================
// OverviewChart — read-only "at a glance" chart for the Overview tab.
// =====================================================================
// The Overview tab is the dentist's first impression of a patient; the
// chart is the most-touched clinical artifact, so it lives here too.
//
// This component is intentionally NON-interactive. Editing happens in
// the dedicated Charting tab. Surface taps do nothing here; the dentist
// clicks the "Open in Charting" link to actually work on it.
//
// Sub-tabs:
//   [Chart] — read-only render of the latest finalized snapshot
//   [Tx Plan] — placeholder until Treatment ships
// =====================================================================

const SUBTABS = [
  { id: "chart",   label: "Chart",   icon: Smile },
  { id: "tx-plan", label: "Tx Plan", icon: FileText },
];

function formatDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
  } catch { return d; }
}

export default function OverviewChart({ patientId, patient, onJumpToTab, tutorialDemoData = null }) {
  const [active, setActive]   = useState("chart");
  const [snapshot, setSnapshot] = useState(null);
  const [findings, setFindings] = useState([]);
  const [planItems, setPlanItems] = useState(null);   // items of the active plan, or null
  const [loading, setLoading]   = useState(() => !tutorialDemoData);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (tutorialDemoData) {
      setSnapshot(tutorialDemoData.snapshot ?? null);
      setFindings(tutorialDemoData.findings ?? []);
      setPlanItems(tutorialDemoData.planItems ?? null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      const [snap, cat, plansRes] = await Promise.all([
        supabase.rpc("get_chart_current",     { p_patient_id: patientId }),
        supabase.rpc("list_chart_findings"),
        supabase.rpc("list_treatment_plans",  { p_patient_id: patientId }),
      ]);
      if (cancelled) return;
      if (snap.error) { setLoading(false); setError(snap.error.message ?? "Could not load chart."); return; }
      if (cat.error)  { setLoading(false); setError(cat.error.message  ?? "Could not load findings."); return; }
      setSnapshot(snap.data ?? null);
      setFindings(cat.data ?? []);

      // Pick the most recent active plan (list is already ORDER BY created_at DESC).
      // Plans RPC errors are non-fatal — the chart still renders without dots.
      const plans = plansRes.error ? [] : (plansRes.data ?? []);
      const activePlan = plans.find((p) => ACTIVE_PLAN_STATUSES.has(p.status));
      if (!activePlan) { setPlanItems(null); setLoading(false); return; }
      const planRes = await supabase.rpc("get_treatment_plan", { p_plan_id: activePlan.id });
      if (cancelled) return;
      setLoading(false);
      setPlanItems(planRes.error ? null : (planRes.data?.items ?? null));
    }
    load();
    return () => { cancelled = true; };
  }, [patientId, tutorialDemoData]);

  const dentitionMode  = defaultDentitionFromDOB(patient?.date_of_birth);
  const planDotsByTooth = useMemo(() => buildPlanDotsByTooth(planItems), [planItems]);
  const completedWorkItems = useMemo(() => snapshot?.completed_work ?? [], [snapshot?.completed_work]);
  const completedWorkByTooth = useMemo(
    () => buildCompletedWorkByTooth(completedWorkItems),
    [completedWorkItems],
  );
  const chartState = useMemo(
    () => applyCompletedWorkChartEffects(snapshot?.state, completedWorkItems),
    [snapshot?.state, completedWorkItems],
  );

  return (
    <section className="bg-white rounded-2xl shadow-sm" data-testid="overview-charting">
      {/* Header — section title + sub-tab pill */}
      <header className="flex flex-wrap items-center gap-3 p-4 sm:p-5 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
            <HistoryIcon className="w-4 h-4 text-teal-600" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Dynamic Charting</p>
            <p className="text-xs text-slate-500 mt-0.5">
              At-a-glance clinical state. Open the Charting tab to edit.
            </p>
          </div>
        </div>

        {/* Sub-tab toggle */}
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="overview-subtabs">
          {SUBTABS.map((t) => {
            const isActive = active === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                data-testid={`overview-subtab-${t.id}`}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Body */}
      <div className="p-4 sm:p-5" data-testid={`overview-subtab-content-${active}`}>
        {active === "chart" ? (
          <ChartView
            loading={loading}
            error={error}
            snapshot={snapshot}
            findings={findings}
            dentitionMode={dentitionMode}
            planDotsByTooth={planDotsByTooth}
            completedWorkByTooth={completedWorkByTooth}
            chartState={chartState}
            onJumpToTab={onJumpToTab}
          />
        ) : (
          <TxPlanPlaceholder onJumpToTab={onJumpToTab} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// Chart sub-tab — read-only render of the latest snapshot
// ---------------------------------------------------------------------
function ChartView({
  loading,
  error,
  snapshot,
  findings,
  dentitionMode,
  planDotsByTooth,
  completedWorkByTooth,
  chartState,
  onJumpToTab,
}) {
  if (loading) {
    return <div data-testid="overview-chart-loading" className="text-sm text-slate-400 px-2 py-6">Loading chart…</div>;
  }
  if (error) {
    return <div data-testid="overview-chart-error" className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>;
  }
  if (!snapshot) {
    return (
      <div data-testid="overview-chart-empty" className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-teal-50 mx-auto flex items-center justify-center">
          <Smile className="w-5 h-5 text-teal-600" strokeWidth={2} />
        </div>
        <h3 className="mt-3 text-sm font-semibold text-slate-900">No chart yet</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
          The patient has never been charted. Open the Charting tab to start a session.
        </p>
        {onJumpToTab ? (
          <button
            type="button"
            onClick={() => onJumpToTab("charting")}
            data-testid="overview-chart-cta-charting"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
          >
            Open Charting tab <ArrowUpRight className="w-3 h-3" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Snapshot meta + jump link */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-slate-500">
          Current chart · Finalized {formatDateTime(snapshot.finalized_at)}
          {snapshot.finalized_by_name ? <> by <span className="text-slate-700">{snapshot.finalized_by_name}</span></> : null}
        </p>
        {onJumpToTab ? (
          <button
            type="button"
            onClick={() => onJumpToTab("charting")}
            data-testid="overview-chart-jump"
            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
          >
            Open in Charting <ArrowUpRight className="w-3 h-3" />
          </button>
        ) : null}
      </div>

      {/* Read-only chart */}
      <div className="overflow-x-auto" data-testid="overview-chart-canvas">
        <ToothChart
          state={chartState}
          findings={findings}
          mode={dentitionMode}
          planDotsByTooth={planDotsByTooth}
          completedWorkByTooth={completedWorkByTooth}
          // Always full arch on overview — at-a-glance is the point.
          // Fluid sizing in ToothChart already shrinks teeth on narrow
          // screens, and overflow-x-auto lets the user pan if it still
          // doesn't fit.
          viewMode="full"
          readOnly
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Tx Plan sub-tab — placeholder until Treatment ships
// ---------------------------------------------------------------------
function TxPlanPlaceholder({ onJumpToTab }) {
  return (
    <div className="text-center py-8" data-testid="overview-txplan-placeholder">
      <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
        <FileText className="w-5 h-5 text-slate-400" />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-slate-900">Treatment Plan preview</h3>
      <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
        The most recent plan summary will live here once the Treatment tab ships.
      </p>
      {onJumpToTab ? (
        <button
          type="button"
          onClick={() => onJumpToTab("treatment")}
          data-testid="overview-txplan-cta-treatment"
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
        >
          Open Treatment tab <ArrowUpRight className="w-3 h-3" />
        </button>
      ) : null}
    </div>
  );
}
